import moment from 'moment';
import { actionsFromList } from '@mgc/map-client-shared/lib/ReduxTools';
import selectors from '../selectors';
import ErrorActions from './error';
import UiActions from './ui';
import UserActions from './user';
import strings from '../strings';
import sentry from '../sentry';
import UploadConfigurationActions from './uploadConfiguration';
import utils from '../utils';

function _assembleTransaction(state) {
    const offer = selectors.offer.getSelectedOffer(state);
    const serversBySite = selectors.offer.getServersGroupedBySite(state);
    const soldTo = selectors.cart.getSoldTo(state);
    const timezoneOffset = moment().utcOffset() / 60;
    //console.log('state order js=',state);
    return {
        amount: selectors.cart.getTotal(state),
        currency: selectors.offer.getSelectedCurrency(state),
        description: 'Software Order',
        instructions: selectors.cart.getInstructions(state),
        licenses: selectors.cart.getCartContents(state)
            .filter((item) => item.addedToCart && !item.saveForLater) // Exclude items with saveForLater === true
            .map((item) => {
                const offerEnd = moment(offer.validTo);
                let validTo = moment(item.startDate).add(item.monthCount, 'months');
                if (offer.enterprise_deal || utils.isDateRangeSoldTO(soldTo) || offer.dailyPricingEnabled) {
                    validTo = moment(item.endDate);
                }else{
                    if (offer.weeklyPricingEnabled && item.weekCount) {
                        validTo = validTo.add(item.weekCount, 'weeks');
                    }
                }
                validTo =  validTo.isAfter(offerEnd) ? offerEnd : validTo;
                                
                return {
                    amount: item.subtotal,
                    duration: item.monthCount,
                    product: item.pricing.product.id,
                    seats: item.seats,
                    server: serversBySite[item.siteIndex].servers[item.serverIndex]
                        .id,
                    site: serversBySite[item.siteIndex].id,
                    timezoneOffset,
                    validFrom: item.startDate,
                    validTo: validTo.toISOString(),
                    weeks: item.weekCount || 0,
                    tiered_discount: item.isTieredDiscountApplied,
                    cartId: item.cartId
                };
            }),
        offer: selectors.offer.getSelectedOfferId(state),
        type: 'order',
    };
}

function _return_date_time_adjusted_with_offset(date, timezoneOffset) {
    let dateTime = timezoneOffset > 0 ? moment(date).utcOffset(0, true).subtract(timezoneOffset, 'hours').toISOString()
        : moment(date).utcOffset(0, true).add(Math.abs(timezoneOffset), 'hours').toISOString();
    return dateTime;
}

function _build_licenses(cart, offer, timezoneOffset) {
    return cart.filter((item) => !item.saveForLater) // Exclude items with saveForLater === true
        .map((item) => {
            const offerEnd = moment(offer.validTo).toISOString();

            // Calculating validFrom and validTo moment date without timezone
            let validFrom = _return_date_time_adjusted_with_offset(item.startDate, timezoneOffset);
            let validTo = _return_date_time_adjusted_with_offset(item.endDate, timezoneOffset);


            // Even though the license end date given as greater than offer end period
            // We should allow license end date only until offer end period, so replace
            // it with offer end date
            if (validTo > offerEnd) {
                validTo = offerEnd;
            }

            return {
                amount: item.subtotal,
                duration: item.monthCount,
                product: item.pricing.product.productId,
                seats: item.seats,
                server: item.serverId,
                site: item.siteId,
                timezoneOffset,
                validFrom: validFrom,
                validTo: validTo,
                weeks: item.weekCount || 0,
                cartId: item.cartId,
            };
        });
}

function _assembleBulkProductExcelUploadTransaction(state, cart) {
    const offer = selectors.offer.getSelectedOffer(state);
    const timezoneOffset = moment().utcOffset() / 60;

    return {
        amount: cart.reduce((sum, item) => sum + item.subtotal, 0),
        currency: selectors.offer.getSelectedCurrency(state),
        description: 'Software Order',
        instructions: selectors.cart.getInstructions(state),
        licenses: _build_licenses(cart, offer, timezoneOffset),
        offer: selectors.offer.getSelectedOfferId(state),
        type: 'order',
        uploadType: 'excel',
        is_excel: Boolean(true),
    };
}

class OrderActions {
    static types = actionsFromList([
        'CANCELED_ORDER',
        'CANCELING_ORDER',
        'DOWNLOADED_TRANSACTION_REPORT',
        'DOWNLOADING_TRANSACTION_REPORT',
        'ORDER_COMPLETE',
        'ORDER_FAILURE',
        'EXCEL_ORDER_FAILURE',
        'ORDER_INPROGRESS',
        'ORDER_PROGRESS_RESET',
        'SELECT_TRANSACTION',
        'CANCELING_EXCEL_ORDER',
        'CANCELED_EXCEL_ORDER',
        'EXCEL_ORDER_INPROGRESS',
        'EXCEL_ORDER_PROGRESS_RESET',
        'EXCEL_ORDER_COMPLETE',
        'DOWNLOADED_CONFIGURATION_LIST',
        'DOWNLOADING_CONFIGURATION_LIST',
    ]);

    // sync --------------------------------------------------------------------

    static resetProgress = () => ({
        type: OrderActions.types.ORDER_PROGRESS_RESET,
    });

    static resetExcelProgress = () => ({
        type: OrderActions.types.EXCEL_ORDER_PROGRESS_RESET,
    });

    static orderInProgress = () => ({
        type: OrderActions.types.ORDER_INPROGRESS,
    });

    static excelOrderInProgress = () => ({
        type: OrderActions.types.EXCEL_ORDER_INPROGRESS,
    });

    static orderComplete = (transaction) => ({
        type: OrderActions.types.ORDER_COMPLETE,
        payload: { transaction },
    });

    static excelOrderComplete = (transaction) => ({
        type: OrderActions.types.EXCEL_ORDER_COMPLETE,
        payload: { transaction },
    });

    static orderFailure = (error) => ({
        type: OrderActions.types.ORDER_FAILURE,
        payload: { error },
    });

    static excelOrderFailure = (error) => ({
        type: OrderActions.types.EXCEL_ORDER_FAILURE,
        payload: { error },
    });

    static cancelingOrder = (transaction) => ({
        type: OrderActions.types.CANCELING_ORDER,
        payload: { transaction },
    });

    static cancelingExcelOrder = (transaction) => ({
        type: OrderActions.types.CANCELING_EXCEL_ORDER,
        payload: { transaction },
    });

    static canceledOrder = (transaction) => ({
        type: OrderActions.types.CANCELED_ORDER,
        payload: { transaction },
    });

    static canceledExcelOrder = (transaction) => ({
        type: OrderActions.types.CANCELED_EXCEL_ORDER,
        payload: { transaction },
    });

    static selectTransaction = (transaction) => ({
        type: OrderActions.types.SELECT_TRANSACTION,
        payload: { transaction },
    });

    static downloadingTransactionReport = () => ({
        type: OrderActions.types.DOWNLOADING_TRANSACTION_REPORT,
    });

    static downloadedTransactionReport = () => ({
        type: OrderActions.types.DOWNLOADED_TRANSACTION_REPORT,
    });

    static downloadingConfigurationProductList = () => ({
        type: OrderActions.types.DOWNLOADING_CONFIGURATION_LIST,
    });

    static downloadedConfigurationProductList = () => ({
        type: OrderActions.types.DOWNLOADED_CONFIGURATION_LIST,
    });

    // async -------------------------------------------------------------------

    static submitOrder = () => (dispatch, getState) => {
        const state = getState();

        dispatch(OrderActions.orderInProgress());

        return state.api
            .putOrder(_assembleTransaction(state))
            .then((result) => {
                if (result.data && result.data.message && result.data.message === 'Request was acceptable but ignored.') {
                    dispatch(OrderActions.orderFailure(result.data));

                } else {
                    dispatch(OrderActions.orderComplete(result.data));
                    // Setting user data with updated cart data into the user object:
                    let user = { data: result.data.user };
                    dispatch(UserActions.receivedUser(user));
                }

            })
            .catch((error) => {
                // Handle errors in order submission
                sentry.logException(error);
                dispatch(OrderActions.orderFailure(error));
            });
    };

    static submitExcelOrder = (cart) => (dispatch, getState) => {
        const state = getState();

        dispatch(OrderActions.excelOrderInProgress());

        return state.api
            .putOrder(_assembleBulkProductExcelUploadTransaction(state, cart))
            .then((result) =>
                dispatch(OrderActions.excelOrderComplete(result.data))
            )
            .catch((error) => {
                sentry.logException(error);
                dispatch(OrderActions.excelOrderFailure(error));
            });
    };

    static cancelExcelOrder =
        (transaction, history) => (dispatch, getState) => {
            const state = getState();

            dispatch(OrderActions.cancelingExcelOrder(transaction));

            return state.api
                .cancelOrder(transaction)
                .then(() => {
                    dispatch(OrderActions.canceledExcelOrder(transaction));
                    dispatch(UiActions.finishExcelOrder(history));
                    dispatch(UiActions.closeModal('cancel'));
                    dispatch(UiActions.closeModal('transaction'));
                })
                .catch(ErrorActions.errorHandler(dispatch));
        };

    static cancelOrder = (transaction, history) => (dispatch, getState) => {
        const state = getState();

        dispatch(OrderActions.cancelingOrder(transaction));

        return state.api
            .cancelOrder(transaction)
            .then(() => {
                dispatch(OrderActions.canceledOrder(transaction));
                dispatch(UiActions.finishOrder(history));
                dispatch(UiActions.closeModal('cancel'));
                dispatch(UiActions.closeModal('transaction'));
            })
            .catch(ErrorActions.errorHandler(dispatch));
    };

    static downloadTransactionReport = (offer) => (dispatch, getState) => {
        const state = getState();
        const filename = `${strings.transactionReportFilename} ${offer.mentorId}.xlsx`;

        dispatch(OrderActions.downloadingTransactionReport());

        state.api
            .downloadOfferTransactionReport(offer.id, filename)
            .then(() => OrderActions.downloadedTransactionReport())
            .catch(ErrorActions.errorHandler(dispatch));
    };

    static downloadConfigurationProductList = (offerId) => (dispatch, getState) => {
        const state = getState();
        const filename = `${strings.configurationTemplate}.xlsm`;

        dispatch(UploadConfigurationActions.setDownloadSpinner(true));

        dispatch(OrderActions.downloadingConfigurationProductList());

        state.api
            .downloadConfigurationProductList(offerId, filename)
            .then(() => {
                OrderActions.downloadedConfigurationProductList();
                dispatch(UploadConfigurationActions.setDownloadSpinner(false));
            })
            .catch(ErrorActions.errorHandler(dispatch));
    };

    static downloadConfigurationProductListWithErrors =
        (fileData) => (dispatch, getState) => {
            const state = getState();
            const filename = `${'ConfigurationTemplateWithErrors'}.xlsm`;

            // dispatch(OrderActions.downloadingConfigurationProductList());

            state.api.downloadConfigurationProductListWithErrors(
                fileData,
                filename
            );
            // .then(() => {})
            // .catch(ErrorActions.errorHandler(dispatch));
        };
}

export default OrderActions;



// WEBPACK FOOTER //
// ./app/actions/order.js